import { useState, useCallback, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  FileText,
  Download,
  History,
  Settings2,
  ChevronRight,
  Search,
  Filter,
  LayoutTemplate,
  RefreshCw,
  Eye,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { FluentCard } from '@/components/layout/page-header'
import { useToast } from '@/components/ui/use-toast'
import { DocumentPreview } from '@/components/document-studio/DocumentPreview'
import { VariableEditor, type TemplateVariable } from '@/components/document-studio/VariableEditor'

interface TemplateOption {
  id: string
  name: string
  type: string
  category: string
  description?: string
  variableCount: number
}

// Mock data for templates - replace with API call
const mockTemplates: TemplateOption[] = [
  { id: '1', name: 'Sales Proposal', type: 'Word', category: 'Sales', description: 'Standard sales proposal template', variableCount: 8 },
  { id: '2', name: 'Employment Contract', type: 'Word', category: 'Legal', description: 'Standard employment agreement', variableCount: 12 },
  { id: '3', name: 'Invoice Template', type: 'Excel', category: 'Finance', description: 'Standard invoice with calculations', variableCount: 6 },
  { id: '4', name: 'Marketing Report', type: 'PowerPoint', category: 'Marketing', description: 'Monthly marketing report', variableCount: 15 },
  { id: '5', name: 'NDA Template', type: 'Word', category: 'Legal', description: 'Non-disclosure agreement', variableCount: 5 },
]

// Mock variables for selected template
const mockVariables: Record<string, TemplateVariable[]> = {
  '1': [
    { id: 'v1', name: 'client_name', displayName: 'Client Name', type: 'text', required: true, category: 'Client Info', aiSuggestionEnabled: true },
    { id: 'v2', name: 'client_company', displayName: 'Company Name', type: 'text', required: true, category: 'Client Info' },
    { id: 'v3', name: 'proposal_date', displayName: 'Proposal Date', type: 'date', required: true, category: 'Document Info' },
    { id: 'v4', name: 'project_name', displayName: 'Project Name', type: 'text', required: true, category: 'Project Details', aiSuggestionEnabled: true },
    { id: 'v5', name: 'project_description', displayName: 'Project Description', type: 'richtext', required: false, category: 'Project Details', validation: { maxLength: 500 }, aiSuggestionEnabled: true },
    { id: 'v6', name: 'total_amount', displayName: 'Total Amount', type: 'number', required: true, category: 'Pricing' },
    { id: 'v7', name: 'payment_terms', displayName: 'Payment Terms', type: 'select', required: true, category: 'Pricing', options: ['Net 15', 'Net 30', 'Net 45', 'Due on Receipt'] },
    { id: 'v8', name: 'include_appendix', displayName: 'Include Appendix', type: 'boolean', required: false, category: 'Options', defaultValue: 'true' },
  ],
  '2': [
    { id: 'v1', name: 'employee_name', displayName: 'Employee Name', type: 'text', required: true, category: 'Employee Info' },
    { id: 'v2', name: 'position', displayName: 'Position/Title', type: 'text', required: true, category: 'Employment Details' },
    { id: 'v3', name: 'start_date', displayName: 'Start Date', type: 'date', required: true, category: 'Employment Details' },
    { id: 'v4', name: 'salary', displayName: 'Annual Salary', type: 'number', required: true, category: 'Compensation' },
  ],
}

export default function DocumentStudio() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const templateIdParam = searchParams.get('template')

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(templateIdParam)
  const [variableValues, setVariableValues] = useState<Record<string, string | number | boolean>>({})
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [previewContent, setPreviewContent] = useState<string | null>(null)
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false)
  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false)
  const [isGeneratingAI, setIsGeneratingAI] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')

  const selectedTemplate = mockTemplates.find((t) => t.id === selectedTemplateId)
  const templateVariables = selectedTemplateId ? mockVariables[selectedTemplateId] || [] : []

  // Filter templates
  const filteredTemplates = mockTemplates.filter((template) => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = categoryFilter === 'all' || template.category === categoryFilter
    return matchesSearch && matchesCategory
  })

  const categories = [...new Set(mockTemplates.map((t) => t.category))]

  // Update URL when template changes
  useEffect(() => {
    if (selectedTemplateId) {
      setSearchParams({ template: selectedTemplateId })
    } else {
      setSearchParams({})
    }
  }, [selectedTemplateId, setSearchParams])

  const handleTemplateSelect = useCallback((templateId: string) => {
    setSelectedTemplateId(templateId)
    setVariableValues({})
    setValidationErrors({})
    setPreviewContent(null)
  }, [])

  const handleVariableChange = useCallback((name: string, value: string | number | boolean) => {
    setVariableValues((prev) => ({ ...prev, [name]: value }))
    setValidationErrors((prev) => {
      const newErrors = { ...prev }
      delete newErrors[name]
      return newErrors
    })
  }, [])

  const validateVariables = useCallback((): boolean => {
    const errors: Record<string, string> = {}

    templateVariables.forEach((variable) => {
      const value = variableValues[variable.name]

      if (variable.required && (value === undefined || value === '')) {
        errors[variable.name] = `${variable.displayName} is required`
      }

      if (variable.validation) {
        if (variable.type === 'text' || variable.type === 'richtext') {
          const strValue = String(value || '')
          if (variable.validation.minLength && strValue.length < variable.validation.minLength) {
            errors[variable.name] = `Minimum ${variable.validation.minLength} characters required`
          }
          if (variable.validation.maxLength && strValue.length > variable.validation.maxLength) {
            errors[variable.name] = `Maximum ${variable.validation.maxLength} characters allowed`
          }
        }

        if (variable.type === 'number') {
          const numValue = Number(value)
          if (variable.validation.min !== undefined && numValue < variable.validation.min) {
            errors[variable.name] = `Minimum value is ${variable.validation.min}`
          }
          if (variable.validation.max !== undefined && numValue > variable.validation.max) {
            errors[variable.name] = `Maximum value is ${variable.validation.max}`
          }
        }
      }
    })

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [templateVariables, variableValues])

  const handleGeneratePreview = useCallback(async () => {
    if (!selectedTemplateId) return

    setIsGeneratingPreview(true)
    try {
      // Simulate API call for preview
      await new Promise((resolve) => setTimeout(resolve, 1500))

      // Generate mock preview content
      let preview = `<h1>${selectedTemplate?.name || 'Document'}</h1>`
      preview += `<p><em>Generated: ${new Date().toLocaleString()}</em></p>`
      preview += `<hr/>`

      templateVariables.forEach((variable) => {
        const value = variableValues[variable.name]
        preview += `<p><strong>${variable.displayName}:</strong> ${value !== undefined ? value : '<em>Not set</em>'}</p>`
      })

      setPreviewContent(preview)
    } catch (error) {
      toast({
        title: 'Preview Error',
        description: 'Failed to generate preview. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsGeneratingPreview(false)
    }
  }, [selectedTemplateId, selectedTemplate, templateVariables, variableValues, toast])

  const handleGenerateDocument = useCallback(async () => {
    if (!validateVariables()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      })
      return
    }

    setIsGeneratingDocument(true)
    try {
      // Simulate API call for document generation
      await new Promise((resolve) => setTimeout(resolve, 2000))

      toast({
        title: 'Document Generated',
        description: `${selectedTemplate?.name} has been generated successfully.`,
      })

      // In production, this would trigger a download or redirect to the document
    } catch (error) {
      toast({
        title: 'Generation Error',
        description: 'Failed to generate document. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsGeneratingDocument(false)
    }
  }, [validateVariables, selectedTemplate, toast])

  const handleAISuggest = useCallback(async (variableName: string): Promise<string> => {
    setIsGeneratingAI(true)
    try {
      // Simulate AI suggestion
      await new Promise((resolve) => setTimeout(resolve, 1000))

      const suggestions: Record<string, string> = {
        client_name: 'John Smith',
        project_name: 'Digital Transformation Initiative',
        project_description: 'A comprehensive project to modernize business processes through digital technologies, improving efficiency and customer experience.',
      }

      return suggestions[variableName] || `AI suggestion for ${variableName}`
    } finally {
      setIsGeneratingAI(false)
    }
  }, [])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-gradient-to-br from-violet-400 to-purple-600 shadow-lg shadow-violet-500/25">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Document Studio</h1>
            <p className="text-gray-500 dark:text-gray-400">
              Create documents from templates with AI-powered assistance
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => navigate('/templates')}>
            <LayoutTemplate className="h-4 w-4 mr-2" />
            Manage Templates
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="rounded-full">
                <History className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 rounded-xl">
              <DropdownMenuLabel>Recent Documents</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="rounded-lg">
                <FileText className="h-4 w-4 mr-2 text-gray-400" />
                Sales Proposal - Acme Corp
              </DropdownMenuItem>
              <DropdownMenuItem className="rounded-lg">
                <FileText className="h-4 w-4 mr-2 text-gray-400" />
                NDA - John Smith
              </DropdownMenuItem>
              <DropdownMenuItem className="rounded-lg">
                <FileText className="h-4 w-4 mr-2 text-gray-400" />
                Invoice #2024-001
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        {/* Template Selection Sidebar */}
        <div className="lg:col-span-3">
          <FluentCard className="sticky top-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
                <LayoutTemplate className="h-4 w-4 text-white" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Templates</h2>
            </div>

            {/* Search and Filter */}
            <div className="space-y-3 mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 rounded-xl"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="rounded-xl">
                  <Filter className="h-4 w-4 mr-2 text-gray-400" />
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="all" className="rounded-lg">All categories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category} className="rounded-lg">
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Template List */}
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {filteredTemplates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleTemplateSelect(template.id)}
                  className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                    selectedTemplateId === template.id
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-transparent bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900 dark:text-white text-sm">
                      {template.name}
                    </span>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="rounded-full text-xs">
                      {template.type}
                    </Badge>
                    <span className="text-xs text-gray-500">
                      {template.variableCount} variables
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </FluentCard>
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-9 space-y-6">
          {selectedTemplate ? (
            <>
              {/* Template Info Bar */}
              <FluentCard className="!p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-purple-500" />
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {selectedTemplate.name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {selectedTemplate.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleGeneratePreview}
                      disabled={isGeneratingPreview}
                      className="rounded-full"
                    >
                      {isGeneratingPreview ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Eye className="h-4 w-4 mr-2" />
                      )}
                      Preview
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleGenerateDocument}
                      disabled={isGeneratingDocument}
                      className="rounded-full bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      {isGeneratingDocument ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      Generate
                    </Button>
                  </div>
                </div>
              </FluentCard>

              <div className="grid gap-6 lg:grid-cols-2">
                {/* Variable Editor */}
                <FluentCard>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-xl bg-gradient-to-br from-amber-400 to-orange-600">
                      <Settings2 className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Variables</h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Fill in the template fields</p>
                    </div>
                  </div>
                  <VariableEditor
                    variables={templateVariables}
                    values={variableValues}
                    errors={validationErrors}
                    onChange={handleVariableChange}
                    onAISuggest={handleAISuggest}
                    isGeneratingAI={isGeneratingAI}
                  />
                </FluentCard>

                {/* Preview */}
                <div className="h-[600px]">
                  <DocumentPreview
                    templateId={selectedTemplateId ?? undefined}
                    templateName={selectedTemplate.name}
                    previewContent={previewContent ?? undefined}
                    isLoading={isGeneratingPreview}
                    onRefresh={handleGeneratePreview}
                    onDownload={handleGenerateDocument}
                    className="h-full"
                  />
                </div>
              </div>
            </>
          ) : (
            <FluentCard className="flex flex-col items-center justify-center py-16">
              <div className="p-4 rounded-2xl bg-gray-100 dark:bg-gray-800 mb-4">
                <LayoutTemplate className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Select a Template
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-center max-w-md">
                Choose a template from the sidebar to start creating your document.
                Fill in the variables and generate your document with AI assistance.
              </p>
            </FluentCard>
          )}
        </div>
      </div>
    </div>
  )
}
